<template>
  <div class="chat-file-uploader">
    <div class="m-3" id="drag-drop"></div>
  </div>
</template>

<script>
import Uppy from '@uppy/core';
import XHRUpload from '@uppy/xhr-upload';
import Dashboard from '@uppy/dashboard';
import AwsS3 from '@uppy/aws-s3';
import { mapActions } from 'vuex';
import { UPLOAD_CONFIG } from '@/config/uploadConfig';
import {
  generateMessageAttachmentPresignedUrl,
  confirmMessageAttachmentUpload,
} from '@/services/messageAttachmentCDNUploadService';

function scrollMessageBodyToEnd() {
  setTimeout(() => {
    const el = document.querySelector('#orderMessageModalBody');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, 500);
}

function triggerUppyDoneButton() {
  setTimeout(() => {
    const doneBtn = window.$ && window.$('#drag-drop').find('.uppy-u-reset.uppy-StatusBar-actionBtn--done');
    if (doneBtn && doneBtn.length) doneBtn.trigger('click');
  }, 300);
}

export default {
  props: ['orderId'],
  data() {
    return {
      uppy: null,
    };
  },

  computed: {},

  mounted() {
    const component = this;
    const orderId = this.orderId;
    const useCDN = UPLOAD_CONFIG.MESSAGE_ATTACHMENT_CDN_UPLOAD ? true : false;

    this.uppy = new Uppy({
      autoProceed: true,
      restrictions: {
        maxNumberOfFiles: 1,
      },
    })
      .use(Dashboard, {
        inline: true,
        target: '#drag-drop',
        hideUploadButton: false,
        browserBackButtonClose: false,
      });

    if (useCDN) {
      this.uppy.use(AwsS3, {
        shouldUseMultipart: () => false,
        async getUploadParameters(file) {
          const data = await generateMessageAttachmentPresignedUrl(orderId, {
            filename: file.name,
            fileType: file.type || 'application/octet-stream',
            fileSize: file.size,
          });

          component.uppy.setFileMeta(file.id, {
            objectKey: data.object_key,
            cdnUrl: data.cdn_url,
          });

          return {
            method: 'PUT',
            url: data.presigned_url,
            headers: {
              'Content-Type': file.type || 'application/octet-stream',
            },
          };
        },
      });
    } else {
      this.uppy.use(XHRUpload, {
        endpoint: `${process.env.VUE_APP_API_URL}/orders/${orderId}/messages/attachment`,
        formData: true,
        fieldName: 'attachment',
        headers: {
          'Authorization': this.$store.state.upload.options.headers.Authorization
        },
      });
    }

    this.uppy.on('upload-success', (fileOrData, response) => {
      if (useCDN) {
        const file = fileOrData;

        confirmMessageAttachmentUpload(orderId, {
          objectKey: file.meta.objectKey,
          cdnUrl: file.meta.cdnUrl,
          filename: file.name,
          fileSize: file.size,
        })
          .then((message) => {
            message.created_at = 'just now';
            component.addMessage(message);
            
            triggerUppyDoneButton();
            scrollMessageBodyToEnd();
          })
          .catch((err) => {
            const msg = err.response?.data?.message || err.message || 'Failed to confirm upload.';
            if (err.response?.status === 400 && msg.toLowerCase().includes('cdn')) {
              console.warn('Message attachment CDN upload is not enabled. Check MESSAGE_ATTACHMENT_CDN_UPLOAD in uploadConfig.js.');
            }

            if (component.uppy) component.uppy.info(msg, 'error', 5000);
          });
      } else {
        const responseBody = response.body;
        responseBody.owner = responseBody.user;
        responseBody.created_at = 'just now';
        delete responseBody.user;

        component.addMessage(responseBody);

        triggerUppyDoneButton();
        scrollMessageBodyToEnd();
      }
    });

    if (useCDN) {
      this.uppy.on('upload-error', (file, error) => {
        const msg = error?.message || 'Upload failed.';

        if (component.uppy) component.uppy.info(msg, 'error', 5000);
      });
    }
  },

  beforeDestroy() {
    if (this.uppy) {
      this.uppy.close();
    }
  },

  methods: {
    ...mapActions({
      addMessage: 'message/addMessage',
    }),
  },
};
</script>

<style lang="css">
/* @import 'https://releases.transloadit.com/uppy/v2.10.0/uppy.min.css'; */

.chat-file-uploader .uppy-Dashboard-inner {
  width: 100% !important;
  height: auto !important;
}

.chat-file-uploader .uppy-DashboardContent-bar {
  display: none;
}

.chat-file-uploader .uploader-container {
  max-width: 280px !important;
}

.chat-file-uploader .uppy-Dashboard-AddFiles-title {
  font-size: 13px;
  margin-top: 5px;
}

.chat-file-uploader .kt-chat .kt-chat__input .kt-chat__toolbar {
  margin-top: -22px;
}
</style>
